import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useCSATNegativeComments } from '@/hooks/useCSATNegativeComments';
import { AlertTriangle, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface NegativeCommentsCardProps {
  daysRange: number;
}

export const NegativeCommentsCard = ({ daysRange }: NegativeCommentsCardProps) => {
  const { data, isLoading } = useCSATNegativeComments(daysRange);
  const [showDetails, setShowDetails] = useState(false);

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-orange-500" />
            Comentários Negativos
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[100px] flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!data || data.totalCount === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-orange-500" />
            Comentários Negativos
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[100px] flex items-center justify-center text-muted-foreground">
            <p>Nenhum dado disponível</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const percentageColor = data.percentage >= 20 
    ? 'text-red-600' 
    : data.percentage >= 10 
    ? 'text-orange-600' 
    : 'text-yellow-600';

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <AlertTriangle className="h-5 w-5 text-orange-500" />
          Comentários Negativos
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-4">
            <div className="text-center p-4 rounded-lg border bg-muted/50">
              <div className="text-2xl font-bold text-foreground">
                {data.negativeCount}
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                Respostas Negativas
              </div>
            </div>
            <div className="text-center p-4 rounded-lg border bg-muted/50">
              <div className={`text-2xl font-bold ${percentageColor}`}>
                {data.percentage}%
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                Percentual
              </div>
            </div>
            <div className="text-center p-4 rounded-lg border bg-muted/50">
              <div className="text-2xl font-bold text-foreground">
                {data.totalCount}
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                Total de Respostas
              </div>
            </div>
          </div>

          {data.negativeResponses.length > 0 && (
            <div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowDetails(!showDetails)}
                className="w-full"
              >
                {showDetails ? (
                  <>
                    <ChevronUp className="h-4 w-4 mr-2" />
                    Ocultar Detalhes
                  </>
                ) : (
                  <>
                    <ChevronDown className="h-4 w-4 mr-2" />
                    Ver Detalhes ({data.negativeResponses.length})
                  </>
                )}
              </Button>

              {showDetails && (
                <div className="mt-4 space-y-3 max-h-[400px] overflow-y-auto">
                  {data.negativeResponses.map((response) => (
                    <div
                      key={response.id}
                      className="p-3 rounded-lg border bg-card"
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium">
                            {response.page_name}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            {response.rating === 1 ? '⭐' : '⭐⭐'} ({response.rating}/5)
                          </span>
                        </div>
                        <span className="text-xs text-muted-foreground">
                          {format(new Date(response.created_at), 'dd/MM/yyyy', { locale: ptBR })}
                        </span>
                      </div>
                      {response.comment && (
                        <p className="text-sm text-foreground mt-2">
                          "{response.comment}"
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

